import { useEditor, EditorContent, type Editor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Placeholder from '@tiptap/extension-placeholder'
import Link from '@tiptap/extension-link'
import Image from '@tiptap/extension-image'
import TaskList from '@tiptap/extension-task-list'
import TaskItem from '@tiptap/extension-task-item'
import Highlight from '@tiptap/extension-highlight'
import Typography from '@tiptap/extension-typography'
import { Table } from '@tiptap/extension-table'
import { TableRow } from '@tiptap/extension-table-row'
import { TableCell } from '@tiptap/extension-table-cell'
import { TableHeader } from '@tiptap/extension-table-header'
import { useState, useEffect, useCallback, useRef } from 'react'
import { api } from '../../../lib/api'
import { useToast } from '../../ui/Toast'
import { Button } from '../../ui/Button'
import { Select } from '../../ui/Select'
import { GlowCard } from '../../ui/GlowCard'
import { EditorToolbar } from './EditorToolbar'
import { SlashCommandMenu } from './SlashCommandMenu'

const PlusIcon = <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>

interface KBEditorV2Props {
  articleSlug: string | null
  categories: { id: string; name: string }[]
  onClose: () => void
  onSaved?: () => void
}

interface Article {
  id: string
  slug: string
  title: string
  content: string
  content_format: string
  article_type: string
  category_id: string | null
  visibility: string
  status: string
  tags: string | null
  revision_count: number
  version: number
  ai_generated: number
}

const ARTICLE_TYPES = [
  { value: 'sop', label: 'SOP' },
  { value: 'site_doc', label: 'Site Documentation' },
  { value: 'product_guide', label: 'Product Guide' },
  { value: 'troubleshooting', label: 'Troubleshooting' },
  { value: 'client_facing', label: 'Client FAQ' },
  { value: 'faq', label: 'FAQ' },
  { value: 'onboarding', label: 'Onboarding' },
]

const VISIBILITY_OPTIONS = [
  { value: 'internal', label: 'Internal' },
  { value: 'client', label: 'Client' },
  { value: 'public', label: 'Public' },
]

export function KBEditorV2({ articleSlug, categories, onClose, onSaved }: KBEditorV2Props) {
  const [article, setArticle] = useState<Article | null>(null)
  const [title, setTitle] = useState('')
  const [articleType, setArticleType] = useState('sop')
  const [visibility, setVisibility] = useState('internal')
  const [categoryId, setCategoryId] = useState('')
  const [tags, setTags] = useState('')
  const [saving, setSaving] = useState(false)
  const [saveStatus, setSaveStatus] = useState<'saved' | 'saving' | 'unsaved' | ''>('')
  const [loading, setLoading] = useState(!!articleSlug)
  const [showProperties, setShowProperties] = useState(true)
  const [showSlashMenu, setShowSlashMenu] = useState(false)
  const [slashMenuPos, setSlashMenuPos] = useState({ top: 0, left: 0 })
  const [aiLoading, setAiLoading] = useState<string | null>(null)
  const [aiPrompt, setAiPrompt] = useState('')
  const [showAiPrompt, setShowAiPrompt] = useState(false)
  const autoSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const { toast, toastError } = useToast()

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Placeholder.configure({
        placeholder: ({ node }) => {
          if (node.type.name === 'heading') return 'Heading'
          return 'Write something, or type / for commands...'
        },
      }),
      Link.configure({ openOnClick: false }),
      Image,
      TaskList,
      TaskItem.configure({ nested: true }),
      Highlight,
      Typography,
      Table.configure({ resizable: true }),
      TableRow,
      TableCell,
      TableHeader,
    ],
    editorProps: {
      attributes: {
        class: 'kb-tiptap-editor',
      },
      handleKeyDown: (_view, event) => {
        // Cmd+S to save
        if ((event.metaKey || event.ctrlKey) && event.key === 's') {
          event.preventDefault()
          handleSave()
          return true
        }
        return false
      },
    },
    onUpdate: ({ editor: ed }) => {
      setSaveStatus('unsaved')
      // Auto-save after 3 seconds of inactivity
      if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current)
      autoSaveTimer.current = setTimeout(() => {
        handleAutoSave(ed)
      }, 3000)
    },
  })

  // Load article
  useEffect(() => {
    if (!articleSlug) {
      setLoading(false)
      return
    }
    api.get<Article>(`/kb/articles/${articleSlug}`)
      .then(res => {
        setArticle(res)
        setTitle(res.title)
        setArticleType(res.article_type)
        setVisibility(res.visibility)
        setCategoryId(res.category_id || '')
        setTags(res.tags || '')
        // Load content into TipTap editor
        if (editor) {
          let loaded = false

          // 1. Try tiptap_content field (V2 format)
          if ((res as any).tiptap_content) {
            try {
              editor.commands.setContent(JSON.parse((res as any).tiptap_content))
              loaded = true
            } catch { /* fallthrough */ }
          }

          // 2. Try sections (legacy format — the primary data source)
          if (!loaded) {
            const sections = (res as any).sections as Array<{ title: string; content: string | null; section_type: string; children?: Array<{ title: string; content: string | null }> }> | undefined
            if (sections && sections.length > 0) {
              const htmlParts: string[] = []
              for (const s of sections) {
                htmlParts.push(`<h2>${s.title}</h2>`)
                if (s.content) {
                  // Fix escaped newlines and convert to HTML paragraphs
                  const cleaned = s.content.replace(/\\n/g, '\n')
                  const paragraphs = cleaned.split('\n\n').filter(Boolean)
                  for (const p of paragraphs) {
                    htmlParts.push(`<p>${p.replace(/\n/g, '<br>')}</p>`)
                  }
                }
                // Include child sections
                if (s.children) {
                  for (const child of s.children) {
                    htmlParts.push(`<h3>${child.title}</h3>`)
                    if (child.content) {
                      const cleaned = child.content.replace(/\\n/g, '\n')
                      htmlParts.push(`<p>${cleaned.replace(/\n/g, '<br>')}</p>`)
                    }
                  }
                }
              }
              if (htmlParts.length > 0) {
                editor.commands.setContent(htmlParts.join(''))
                loaded = true
              }
            }
          }

          // 3. Fallback: raw content field
          if (!loaded && res.content) {
            editor.commands.setContent(res.content)
          }
        }
        setSaveStatus('saved')
      })
      .catch(err => toastError('Failed to load article', err))
      .finally(() => setLoading(false))
  }, [articleSlug, editor, toastError])

  const handleAutoSave = useCallback(async (ed: Editor) => {
    if (!article) return
    setSaving(true)
    setSaveStatus('saving')
    try {
      // Update article metadata
      await api.patch(`/kb/articles/${article.slug}`, {
        title, article_type: articleType, visibility,
        category_id: categoryId || null, tags: tags || null,
      })
      // Update first section with HTML content
      const articleData = await api.get<any>(`/kb/articles/${article.slug}`)
      const sections = articleData.sections || []
      if (sections.length > 0) {
        await api.patch(`/kb/sections/${sections[0].id}`, {
          content: ed.getHTML(),
        })
      }
      setSaveStatus('saved')
    } catch {
      setSaveStatus('unsaved')
    } finally {
      setSaving(false)
    }
  }, [article, title, articleType, visibility, categoryId, tags])

  const handleSave = useCallback(async () => {
    setSaving(true)
    setSaveStatus('saving')
    try {
      const htmlContent = editor ? editor.getHTML() : ''

      if (article) {
        // Update article metadata
        await api.patch(`/kb/articles/${article.slug}`, {
          title, article_type: articleType, visibility,
          category_id: categoryId || null, tags: tags || null,
        })

        // Update the first section's content with editor HTML
        // This preserves backward compat with the section-based viewer
        const articleData = await api.get<any>(`/kb/articles/${article.slug}`)
        const sections = articleData.sections || []
        if (sections.length > 0) {
          // Update first section with full content
          await api.patch(`/kb/sections/${sections[0].id}`, {
            title: sections[0].title,
            content: htmlContent,
          })
        }
        setSaveStatus('saved')
        toast('Saved')
      } else {
        // Create new article
        const res = await api.post<Article>('/kb/articles', {
          title: title || 'Untitled',
          article_type: articleType, visibility,
          category_id: categoryId || undefined, tags: tags || undefined,
        })
        setArticle(res)

        // Add initial section with content
        const full = await api.get<any>(`/kb/articles/${res.slug}`)
        if (full.sections?.length > 0 && htmlContent) {
          await api.patch(`/kb/sections/${full.sections[0].id}`, {
            content: htmlContent,
          })
        }
        setSaveStatus('saved')
        toast('Article created')
      }
      onSaved?.()
    } catch (err) {
      toastError('Failed to save', err)
      setSaveStatus('unsaved')
    } finally {
      setSaving(false)
    }
  }, [editor, article, title, articleType, visibility, categoryId, tags, toast, toastError, onSaved])

  const handlePublish = useCallback(async () => {
    if (!article) {
      await handleSave()
      return
    }
    try {
      await handleSave()
      await api.post(`/kb/articles/${article.slug}/publish`)
      toast('Published')
      onClose()
    } catch (err) {
      toastError('Failed to publish', err)
    }
  }, [article, handleSave, toast, toastError, onClose])

  // Slash command handler
  const handleSlashCommand = useCallback((command: string) => {
    if (!editor) return
    setShowSlashMenu(false)

    switch (command) {
      case 'heading1': editor.chain().focus().toggleHeading({ level: 1 }).run(); break
      case 'heading2': editor.chain().focus().toggleHeading({ level: 2 }).run(); break
      case 'heading3': editor.chain().focus().toggleHeading({ level: 3 }).run(); break
      case 'bullet': editor.chain().focus().toggleBulletList().run(); break
      case 'numbered': editor.chain().focus().toggleOrderedList().run(); break
      case 'checklist': editor.chain().focus().toggleTaskList().run(); break
      case 'code': editor.chain().focus().toggleCodeBlock().run(); break
      case 'blockquote': editor.chain().focus().toggleBlockquote().run(); break
      case 'divider': editor.chain().focus().setHorizontalRule().run(); break
      case 'image': {
        const url = prompt('Image URL:')
        if (url) editor.chain().focus().setImage({ src: url }).run()
        break
      }
      case 'table': editor.chain().focus().insertTable({ rows: 3, cols: 3 }).run(); break
      case 'ai-write': setShowAiPrompt(true); break
    }
  }, [editor])

  // AI handlers
  const handleAiWrite = useCallback(async () => {
    if (!editor || !aiPrompt.trim()) return
    setAiLoading('write')
    try {
      const res = await api.post<{ content: string }>('/kb/ai/write', { prompt: aiPrompt })
      editor.commands.insertContent(res.content)
      setShowAiPrompt(false)
      setAiPrompt('')
      toast('AI content inserted')
    } catch (err) { toastError('AI writing failed', err) }
    finally { setAiLoading(null) }
  }, [editor, aiPrompt, toast, toastError])

  const handleAiImprove = useCallback(async () => {
    if (!editor) return
    const { from, to } = editor.state.selection
    const selectedText = editor.state.doc.textBetween(from, to)
    if (!selectedText.trim()) { toast('Select some text first', 'info'); return }
    setAiLoading('improve')
    try {
      const res = await api.post<{ content: string }>('/kb/ai/improve', { text: selectedText })
      editor.chain().focus().deleteSelection().insertContent(res.content).run()
      toast('Text improved')
    } catch (err) { toastError('AI improvement failed', err) }
    finally { setAiLoading(null) }
  }, [editor, toast, toastError])

  const handleAiSimplify = useCallback(async () => {
    if (!editor) return
    const { from, to } = editor.state.selection
    const selectedText = editor.state.doc.textBetween(from, to)
    if (!selectedText.trim()) { toast('Select some text first', 'info'); return }
    setAiLoading('simplify')
    try {
      const res = await api.post<{ content: string }>('/kb/ai/simplify', { text: selectedText })
      editor.chain().focus().deleteSelection().insertContent(res.content).run()
      toast('Text simplified')
    } catch (err) { toastError('AI simplification failed', err) }
    finally { setAiLoading(null) }
  }, [editor, toast, toastError])

  if (loading) {
    return (
      <div style={{ padding: 40 }}>
        <div className="skeleton" style={{ height: 32, width: 300, marginBottom: 16 }} />
        <div className="skeleton" style={{ height: 400 }} />
      </div>
    )
  }

  return (
    <div className="page-enter">
      {/* Header — sticky */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        position: 'sticky', top: -24, zIndex: 10,
        background: 'var(--bg-0)', paddingTop: 24, marginTop: -24,
        paddingBottom: 16, marginBottom: 20,
        marginLeft: -24, marginRight: -24, paddingLeft: 24, paddingRight: 24,
        borderBottom: '1px solid var(--border)',
      }}>
        <Button onClick={onClose} variant="ghost" size="sm">&larr; Back</Button>
        <div style={{ flex: 1 }} />
        <span style={{
          fontSize: 12, fontFamily: 'var(--mono)',
          color: saveStatus === 'saving' ? 'var(--accent)' : saveStatus === 'saved' ? 'var(--text-3)' : saveStatus === 'unsaved' ? 'var(--warn)' : 'var(--text-3)',
        }}>
          {saveStatus === 'saving' ? 'Saving...' : saveStatus === 'saved' ? 'Saved' : saveStatus === 'unsaved' ? 'Unsaved changes' : ''}
        </span>
        <Button variant="secondary" size="sm" onClick={handleSave} loading={saving}>Save</Button>
        {article && article.status !== 'published' && (
          <Button variant="success" size="sm" onClick={handlePublish}>Publish</Button>
        )}
      </div>

      {/* Editor area */}
      <div className="kb-tiptap-editor-wrap" style={{ maxWidth: 720, margin: '0 auto' }}>
        {/* Title */}
        <input
          className="v3-input"
          value={title}
          onChange={e => { setTitle(e.target.value); setSaveStatus('unsaved') }}
          placeholder="Untitled Article"
          style={{
            fontSize: 28, fontWeight: 700, border: 'none', borderRadius: 0,
            background: 'transparent', padding: '8px 0', marginBottom: 16,
            boxShadow: 'none', color: 'var(--text-1)',
          }}
        />

        {/* Toolbar */}
        {editor && <EditorToolbar editor={editor} />}

        {/* AI Actions */}
        <div style={{
          display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap',
          padding: '8px 12px', background: 'linear-gradient(135deg, rgba(251,191,36,.04), rgba(251,191,36,.01))',
          border: '1px solid rgba(251,191,36,.12)', borderRadius: 8,
        }}>
          <span style={{
            fontSize: 10, fontWeight: 700, color: 'var(--accent)',
            background: 'var(--accent-m)', padding: '2px 8px', borderRadius: 9999,
            letterSpacing: '.06em',
          }}>✨ AI</span>
          <Button variant="secondary" size="sm" onClick={() => setShowAiPrompt(true)} loading={aiLoading === 'write'}
            title="Describe what to write and AI will generate content for you"
          >
            Generate Content
          </Button>
          <Button variant="secondary" size="sm" onClick={handleAiImprove} loading={aiLoading === 'improve'}
            title="Select text first, then click to rewrite it for clarity and professionalism"
          >
            Improve Selected
          </Button>
          <Button variant="secondary" size="sm" onClick={handleAiSimplify} loading={aiLoading === 'simplify'}
            title="Select text first, then click to rewrite it for non-technical readers"
          >
            Simplify Selected
          </Button>
          <span style={{ fontSize: 11, color: 'var(--text-3)', marginLeft: 'auto' }}>Select text first for Improve/Simplify</span>
        </div>

        {/* AI Write Prompt */}
        {showAiPrompt && (
          <div style={{
            padding: 12, marginBottom: 12, background: 'var(--bg-2)',
            border: '1px solid var(--accent)', borderRadius: 8,
          }}>
            <div className="v3-input-wrap" style={{ marginBottom: 8 }}>
              <input
                className="v3-input" autoFocus
                placeholder="Describe what to write... e.g. 'Step-by-step guide for configuring UniFi Protect cameras'"
                value={aiPrompt} onChange={e => setAiPrompt(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleAiWrite() }}
              />
            </div>
            <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
              <Button variant="ghost" size="sm" onClick={() => { setShowAiPrompt(false); setAiPrompt('') }}>Cancel</Button>
              <Button size="sm" onClick={handleAiWrite} loading={aiLoading === 'write'} disabled={!aiPrompt.trim()}>Generate</Button>
            </div>
          </div>
        )}

        {/* TipTap editor */}
        <GlowCard style={{ padding: '20px 24px', minHeight: 400, position: 'relative' }}>
          <EditorContent editor={editor} />
          {showSlashMenu && (
            <SlashCommandMenu
              position={slashMenuPos}
              onSelect={handleSlashCommand}
              onClose={() => setShowSlashMenu(false)}
            />
          )}
        </GlowCard>

        {/* Properties panel */}
        <div style={{ marginTop: 20 }}>
          <button
            onClick={() => setShowProperties(!showProperties)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 11, fontWeight: 600, color: 'var(--text-3)',
              textTransform: 'uppercase', letterSpacing: '.04em',
              fontFamily: 'var(--font)', display: 'flex', alignItems: 'center', gap: 6,
            }}
          >
            <span style={{
              fontSize: 8, transform: showProperties ? 'rotate(90deg)' : 'none',
              transition: 'transform 150ms', display: 'inline-block',
            }}>&#9654;</span>
            Properties
          </button>

          {showProperties && (
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12,
              marginTop: 12, padding: '16px 20px',
              background: 'var(--bg-2)', borderRadius: 'var(--r-m)',
              border: '1px solid var(--border)',
            }}>
              <Select label="Category" value={categoryId}
                onChange={e => { setCategoryId(e.target.value); setSaveStatus('unsaved') }}
                options={[
                  { value: '', label: 'Uncategorized' },
                  ...categories.map(c => ({ value: c.id, label: c.name })),
                ]}
              />
              <Select label="Visibility" value={visibility}
                onChange={e => { setVisibility(e.target.value); setSaveStatus('unsaved') }}
                options={VISIBILITY_OPTIONS}
              />
              <Select label="Type" value={articleType}
                onChange={e => { setArticleType(e.target.value); setSaveStatus('unsaved') }}
                options={ARTICLE_TYPES}
              />
              <div className="v3-field" style={{ gridColumn: '1 / -1' }}>
                <label className="v3-field-label">Tags</label>
                <div className="v3-input-wrap">
                  <input className="v3-input" value={tags} placeholder="comma, separated, tags"
                    onChange={e => { setTags(e.target.value); setSaveStatus('unsaved') }}
                  />
                </div>
              </div>
              {article && (
                <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 16, fontSize: 11, color: 'var(--text-3)' }}>
                  <span>v{article.version || article.revision_count}</span>
                  <span>Status: {article.status}</span>
                  {article.ai_generated ? <span style={{ color: 'var(--accent)' }}>AI Generated</span> : null}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Editor styles */}
      <style>{`
        /* Toolbar button visibility fix */
        .kb-tiptap-editor-wrap .v3-btn-ghost {
          color: var(--text-1);
          opacity: 0.7;
        }
        .kb-tiptap-editor-wrap .v3-btn-ghost:hover {
          opacity: 1;
          background: rgba(255,255,255,.08) !important;
        }
        .kb-tiptap-editor {
          outline: none;
          font-family: var(--font);
          font-size: 15px;
          line-height: 1.7;
          color: var(--text-1);
          min-height: 350px;
        }
        .kb-tiptap-editor h1 { font-size: 24px; font-weight: 700; margin: 24px 0 8px; }
        .kb-tiptap-editor h2 { font-size: 20px; font-weight: 600; margin: 20px 0 6px; }
        .kb-tiptap-editor h3 { font-size: 16px; font-weight: 600; margin: 16px 0 4px; }
        .kb-tiptap-editor p { margin: 0 0 8px; }
        .kb-tiptap-editor ul, .kb-tiptap-editor ol { padding-left: 24px; margin: 0 0 8px; }
        .kb-tiptap-editor li { margin: 2px 0; }
        .kb-tiptap-editor blockquote {
          border-left: 3px solid var(--accent);
          padding-left: 16px;
          margin: 12px 0;
          color: var(--text-2);
          font-style: italic;
        }
        .kb-tiptap-editor pre {
          background: var(--bg-0);
          border: 1px solid var(--border);
          border-radius: 8px;
          padding: 16px;
          font-family: var(--mono);
          font-size: 13px;
          overflow-x: auto;
          margin: 12px 0;
        }
        .kb-tiptap-editor code {
          background: var(--bg-3);
          padding: 2px 6px;
          border-radius: 4px;
          font-family: var(--mono);
          font-size: 13px;
        }
        .kb-tiptap-editor pre code {
          background: none;
          padding: 0;
        }
        .kb-tiptap-editor img {
          max-width: 100%;
          border-radius: 8px;
          margin: 12px 0;
        }
        .kb-tiptap-editor hr {
          border: none;
          border-top: 1px solid var(--border);
          margin: 24px 0;
        }
        .kb-tiptap-editor table {
          border-collapse: collapse;
          width: 100%;
          margin: 12px 0;
        }
        .kb-tiptap-editor th, .kb-tiptap-editor td {
          border: 1px solid var(--border);
          padding: 8px 12px;
          text-align: left;
          font-size: 13px;
        }
        .kb-tiptap-editor th {
          background: var(--bg-3);
          font-weight: 600;
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: .04em;
          color: var(--text-2);
        }
        .kb-tiptap-editor ul[data-type="taskList"] {
          list-style: none;
          padding-left: 0;
        }
        .kb-tiptap-editor ul[data-type="taskList"] li {
          display: flex;
          align-items: flex-start;
          gap: 8px;
        }
        .kb-tiptap-editor ul[data-type="taskList"] li input[type="checkbox"] {
          accent-color: var(--accent);
          margin-top: 4px;
        }
        .kb-tiptap-editor mark {
          background: var(--accent-m);
          color: var(--text-1);
          padding: 1px 4px;
          border-radius: 2px;
        }
        .kb-tiptap-editor .is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          float: left;
          color: var(--text-3);
          pointer-events: none;
          height: 0;
        }
        .kb-tiptap-editor a {
          color: var(--accent);
          text-decoration: underline;
          text-underline-offset: 2px;
        }
      `}</style>
    </div>
  )
}
